import { useCallback } from 'react'
import { useSearchParams } from 'react-router'

import type { ReturnStatus } from './returnStatusConfig'

const VALID_TAB_STATUSES: ReadonlySet<string> = new Set<ReturnStatus>([
  'REQUESTED',
  'APPROVED',
  'REJECTED',
])

const DEFAULT_TAB: ReturnStatus = 'REQUESTED'

function isValidTabStatus(value: string): value is ReturnStatus {
  return VALID_TAB_STATUSES.has(value)
}

function parseStatus(value: string | null): ReturnStatus {
  if (value && isValidTabStatus(value)) {
    return value
  }
  return DEFAULT_TAB
}

function parsePage(value: string | null): number {
  const num = Number(value)
  return Number.isInteger(num) && num >= 1 ? num : 1
}

/**
 * The query string is the single source of truth for the 반품 요청 list state — see
 * `useOrderListUrlSync` for why mirroring component state back into the URL was wrong.
 */
export function useReturnListUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams()

  const activeTab = parseStatus(searchParams.get('status'))
  const currentPage = parsePage(searchParams.get('page'))

  const setTab = useCallback(
    (tab: ReturnStatus) => {
      setSearchParams({ status: tab })
    },
    [setSearchParams],
  )

  const setPage = useCallback(
    (page: number) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous)
        if (page > 1) next.set('page', String(page))
        else next.delete('page')
        return next
      })
    },
    [setSearchParams],
  )

  return { activeTab, currentPage, setTab, setPage } as const
}
